import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapCostCenter, SapGlAccount, SapBudgetRecord } from '@/types/domain/sap-simulator';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/utils';
import { Layers, DollarSign, Building } from 'lucide-react';

export const SapMastersPage: React.FC = () => {
  const [costCenters, setCostCenters] = useState<SapCostCenter[]>([]);
  const [glAccounts, setGlAccounts] = useState<SapGlAccount[]>([]);
  const [budgets, setBudgets] = useState<SapBudgetRecord[]>([]);

  useEffect(() => {
    const loadMasters = async () => {
      const [cc, gl, bdg] = await Promise.all([
        mockSapSimulatorService.getCostCenters(),
        mockSapSimulatorService.getGlAccounts(),
        mockSapSimulatorService.getBudgets(),
      ]);
      setCostCenters(cc);
      setGlAccounts(gl);
      setBudgets(bdg);
    };
    loadMasters();
  }, []);

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" /> Organizational & Financial Masters
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Cost Centers, GL Accounts, and Budget structures for enterprise SAP validation.
          </p>
        </div>

        {/* Cost Centers & GL Accounts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-border rounded-2xl shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/60">
              <CardTitle className="font-heading text-xs font-bold text-foreground uppercase flex items-center gap-2">
                <Building className="h-4 w-4 text-primary" /> Cost Centers ({costCenters.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-2">
              {costCenters.map((cc) => (
                <div key={cc.costCenterCode} className="flex items-center justify-between p-3 rounded-xl border border-border bg-card text-xs">
                  <div>
                    <span className="font-mono font-extrabold text-primary">{cc.costCenterCode}</span>
                    <p className="font-bold text-foreground">{cc.description}</p>
                    <p className="text-[11px] text-foreground-secondary">
                      Dept: {cc.departmentName} • Plant: {cc.plantCode} • Co: {cc.companyCode}
                    </p>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
                    {cc.status}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-border rounded-2xl shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/60">
              <CardTitle className="font-heading text-xs font-bold text-foreground uppercase flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-purple-600" /> GL Accounts ({glAccounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-2">
              {glAccounts.map((gl) => (
                <div key={gl.glAccountCode} className="flex items-center justify-between p-3 rounded-xl border border-border bg-card text-xs">
                  <div>
                    <span className="font-mono font-extrabold text-purple-600">{gl.glAccountCode}</span>
                    <p className="font-bold text-foreground">{gl.description}</p>
                    <p className="text-[11px] text-foreground-secondary">
                      Account Type: {gl.accountType} • Company Code: {gl.companyCode}
                    </p>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
                    {gl.status}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Budget Information Card */}
        <Card className="border-border rounded-2xl shadow-2xs">
          <CardHeader className="pb-3 border-b border-border/60">
            <CardTitle className="font-heading text-xs font-bold text-foreground uppercase">
              SAP Budget Information & Allocation Status
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {budgets.map((bdg) => (
              <div key={bdg.budgetId} className="p-4 rounded-xl border border-border bg-card space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-primary">{bdg.budgetId}</span>
                    <span className="font-bold text-foreground">
                      {bdg.departmentName} ({bdg.costCenterCode})
                    </span>
                  </div>
                  <span
                    className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                      bdg.status === 'SUFFICIENT' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'
                    }`}
                  >
                    {bdg.status}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border/40 text-center">
                  <div>
                    <span className="text-[10px] font-bold text-foreground-muted uppercase block">Allocated</span>
                    <span className="font-extrabold text-foreground">{formatCurrency(bdg.allocatedAmount, 'INR')}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-foreground-muted uppercase block">Utilized</span>
                    <span className="font-extrabold text-foreground">{formatCurrency(bdg.utilizedAmount, 'INR')}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-foreground-muted uppercase block">Committed</span>
                    <span className="font-extrabold text-foreground">{formatCurrency(bdg.committedAmount, 'INR')}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-foreground-muted uppercase block">Available</span>
                    <span className="font-extrabold text-emerald-600">{formatCurrency(bdg.availableAmount, 'INR')}</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </SAPLayout>
  );
};
